/**
 * Currency Configuration
 * Contains currency-specific stakes, min/max bet limits, and display settings
 */

export interface CurrencyConfig {
  code: string;
  symbol: string;
  name: string;
  stakes: number[];
  minBet: number;
  maxBet: number;
  stepAmount: number; // Amount to add/subtract with +/- buttons
  decimals: number;
}

export const CURRENCIES: Record<string, CurrencyConfig> = {
  // Major Currencies
  USD: {
    code: "USD",
    symbol: "$",
    name: "US Dollar",
    stakes: [10, 20, 50, 100],
    minBet: 1,
    maxBet: 20000,
    stepAmount: 10,
    decimals: 2,
  },
  EUR: {
    code: "EUR",
    symbol: "€",
    name: "Euro",
    stakes: [10, 20, 50, 100],
    minBet: 1,
    maxBet: 20000,
    stepAmount: 10,
    decimals: 2,
  },
  GBP: {
    code: "GBP",
    symbol: "£",
    name: "British Pound",
    stakes: [10, 20, 50, 100],
    minBet: 1,
    maxBet: 20000,
    stepAmount: 10,
    decimals: 2,
  },

  // South Asian Currencies
  PKR: {
    code: "PKR",
    symbol: "Rs",
    name: "Pakistani Rupee",
    stakes: [100, 200, 300, 400],
    minBet: 100,
    maxBet: 50000,
    stepAmount: 100,
    decimals: 0,
  },
  INR: {
    code: "INR",
    symbol: "₹",
    name: "Indian Rupee",
    stakes: [100, 200, 300, 400],
    minBet: 100,
    maxBet: 50000,
    stepAmount: 100,
    decimals: 0,
  },
  BDT: {
    code: "BDT",
    symbol: "৳",
    name: "Bangladeshi Taka",
    stakes: [100, 200, 300, 400],
    minBet: 100,
    maxBet: 50000,
    stepAmount: 100,
    decimals: 0,
  },
  NPR: {
    code: "NPR",
    symbol: "रू",
    name: "Nepalese Rupee",
    stakes: [100, 200, 300, 400],
    minBet: 100,
    maxBet: 50000,
    stepAmount: 100,
    decimals: 0,
  },
  LKR: {
    code: "LKR",
    symbol: "Rs",
    name: "Sri Lankan Rupee",
    stakes: [100, 200, 300, 400],
    minBet: 100,
    maxBet: 50000,
    stepAmount: 100,
    decimals: 0,
  },

  // Middle East
  AED: {
    code: "AED",
    symbol: "د.إ",
    name: "UAE Dirham",
    stakes: [10, 20, 50, 100],
    minBet: 5,
    maxBet: 75000,
    stepAmount: 10,
    decimals: 2,
  },
  SAR: {
    code: "SAR",
    symbol: "﷼",
    name: "Saudi Riyal",
    stakes: [10, 20, 50, 100],
    minBet: 5,
    maxBet: 75000,
    stepAmount: 10,
    decimals: 2,
  },

  // Asian Currencies
  CNY: {
    code: "CNY",
    symbol: "¥",
    name: "Chinese Yuan",
    stakes: [10, 20, 50, 100],
    minBet: 10,
    maxBet: 150000,
    stepAmount: 10,
    decimals: 2,
  },
  JPY: {
    code: "JPY",
    symbol: "¥",
    name: "Japanese Yen",
    stakes: [100, 500, 1000, 2000],
    minBet: 100,
    maxBet: 3000000,
    stepAmount: 100,
    decimals: 0,
  },
  KRW: {
    code: "KRW",
    symbol: "₩",
    name: "South Korean Won",
    stakes: [1000, 2000, 5000, 10000],
    minBet: 1000,
    maxBet: 25000000,
    stepAmount: 1000,
    decimals: 0,
  },
  THB: {
    code: "THB",
    symbol: "฿",
    name: "Thai Baht",
    stakes: [50, 100, 200, 500],
    minBet: 50,
    maxBet: 700000,
    stepAmount: 50,
    decimals: 2,
  },
  MYR: {
    code: "MYR",
    symbol: "RM",
    name: "Malaysian Ringgit",
    stakes: [10, 20, 50, 100],
    minBet: 5,
    maxBet: 90000,
    stepAmount: 10,
    decimals: 2,
  },
  IDR: {
    code: "IDR",
    symbol: "Rp",
    name: "Indonesian Rupiah",
    stakes: [10000, 20000, 50000, 100000],
    minBet: 10000,
    maxBet: 300000000,
    stepAmount: 10000,
    decimals: 0,
  },
  PHP: {
    code: "PHP",
    symbol: "₱",
    name: "Philippine Peso",
    stakes: [50, 100, 200, 500],
    minBet: 50,
    maxBet: 1000000,
    stepAmount: 50,
    decimals: 2,
  },
  VND: {
    code: "VND",
    symbol: "₫",
    name: "Vietnamese Dong",
    stakes: [20000, 50000, 100000, 200000],
    minBet: 20000,
    maxBet: 500000000,
    stepAmount: 10000,
    decimals: 0,
  },

  // Other Currencies
  BRL: {
    code: "BRL",
    symbol: "R$",
    name: "Brazilian Real",
    stakes: [10, 20, 50, 100],
    minBet: 5,
    maxBet: 100000,
    stepAmount: 10,
    decimals: 2,
  },
  CAD: {
    code: "CAD",
    symbol: "C$",
    name: "Canadian Dollar",
    stakes: [10, 20, 50, 100],
    minBet: 1,
    maxBet: 25000,
    stepAmount: 10,
    decimals: 2,
  },
  AUD: {
    code: "AUD",
    symbol: "A$",
    name: "Australian Dollar",
    stakes: [10, 20, 50, 100],
    minBet: 1,
    maxBet: 30000,
    stepAmount: 10,
    decimals: 2,
  },
};

// Default currency
export const DEFAULT_CURRENCY = "USD";

// Get currency config by code (with fallback to USD)
export const getCurrencyConfig = (code: string): CurrencyConfig => {
  const upperCode = code?.toUpperCase() || DEFAULT_CURRENCY;
  return CURRENCIES[upperCode] || CURRENCIES[DEFAULT_CURRENCY];
};

// Get list of all supported currency codes
export const getSupportedCurrencies = (): string[] => {
  return Object.keys(CURRENCIES);
};

// Check if currency is supported
export const isCurrencySupported = (code: string): boolean => {
  return code?.toUpperCase() in CURRENCIES;
};

// Format amount based on currency
export const formatAmount = (amount: number, currencyCode: string): string => {
  const config = getCurrencyConfig(currencyCode);
  return amount.toFixed(config.decimals);
};

// Format with currency symbol
export const formatWithSymbol = (amount: number, currencyCode: string): string => {
  const config = getCurrencyConfig(currencyCode);
  return `${config.symbol}${formatAmount(amount, currencyCode)}`;
};
